import dayjs from "dayjs";
import services from "../services";

const strict = true;

const defaultState = function () {
  return {
    change: null,
    highlightedMonthsNum: 0,
    coverages: [],
    currentMonth: {},
    isCurrentMonthCovered: false,
    nextMonth: {},
    isNextMonthCovered: false,
    coverages12months: [],
    previousMonths: [],
    canAskForCoverage: false,
    ceilings: [],
    years: [],
    yearFilter: [{ id: dayjs().year(), label: dayjs().year().toString() }],
    previousMonthsForRimborsiMHAndUS: [],
    isSanimpresaMemberForCoverage: false,
    hasEmvaCoverage: false,
    coverageMonthsForNucleoFamiliareFull: []
  };
};

const state = defaultState();

const getters = {
  change: state => state.change,
  getHighlightedMonthsNum: state => state.highlightedMonthsNum,
  getCoverages: state => state.coverages,
  getCurrentMonth: state => state.currentMonth,
  getIsCurrentMonthCovered: state => state.isCurrentMonthCovered,
  getNextMonth: state => state.nextMonth,
  getIsNextMonthCovered: state => state.isNextMonthCovered,
  getCoverages12months: state => state.coverages12months,
  getPreviousMonths: state => state.previousMonths,
  getCanAskForCoverage: state => state.canAskForCoverage,
  getCeilings: state => state.ceilings,
  getYears: state => state.years,
  getYearFilter: state => state.yearFilter,
  getPreviousMonthsForRimborsiMHAndUS: state =>
    state.previousMonthsForRimborsiMHAndUS,
  isSanimpresaMemberForCoverage: state => state.isSanimpresaMemberForCoverage,
  getHasEmvaCoverage: state => state.hasEmvaCoverage,
  getCoverageMonthsForNucleoFamiliareFull: state =>
    state.coverageMonthsForNucleoFamiliareFull
};

const mutations = {
  reset(state) {
    const s = defaultState();
    Object.keys(s).forEach(key => {
      state[key] = s[key];
    });
  },
  setChange(state, { change }) {
    state.change = change;
  },
  setHighlightedMonthsNum(state, num) {
    state.highlightedMonthsNum = num;
  },
  setCoverages(state, coverages) {
    let allCoverages = [...coverages];
    let currentCoverage = allCoverages.splice(0, 4);
    state.coverages = currentCoverage;
    state.coverages12months = coverages;
    // check if there is almost a month with pacchetto SANI
    let saniMonthIsPresent = currentCoverage.some(m =>
      m.codicePacchetto.includes("SANI")
    );
    state.isSanimpresaMemberForCoverage = saniMonthIsPresent;
  },
  setIsCurrentMonthCovered(state, coverages) {
    let currentMonth = dayjs().month() + 1;
    let currentYear = dayjs().year();
    let currentMonthYearCoverage = coverages.find(
      m =>
        m.year === currentYear &&
        m.month === currentMonth &&
        m.codicePacchetto.includes("FE1") &&
        m.covered
    );
    state.currentMonth = currentMonthYearCoverage || null;
    state.isCurrentMonthCovered = state.currentMonth != null || false;
    let currentDate = dayjs(`${currentYear}-${currentMonth}-01`);
    let nextDate = dayjs(currentDate).add(1, "month");
    let nextMonth = dayjs(nextDate).month() + 1;
    let nextYear = dayjs(nextDate).year();
    let nextMonthYearCoverage = coverages.find(
      m =>
        m.year === nextYear &&
        m.month === nextMonth &&
        m.codicePacchetto.includes("FE1") &&
        m.covered
    );
    state.nextMonth = nextMonthYearCoverage || null;
    state.isNextMonthCovered = state.nextMonth != null || false;
  },
  setCanAskForCoverage(state, value) {
    state.canAskForCoverage = value;
  },
  setPreviousMonths(state, months) {
    state.previousMonths = months;
  },
  ceilings(state, ceilings) {
    state.ceilings = ceilings;
  },
  yearsRetrieved(state, years) {
    state.years = years;
  },
  updateYearFilterField(state, value) {
    state.yearFilter = value;
  },
  setPreviousMonthsForRimborsiMHAndUS(state, months) {
    state.previousMonthsForRimborsiMHAndUS = months;
  },
  setHasEmvaCoverage(state, emvaMonths) {
    state.hasEmvaCoverage = emvaMonths.some(m =>
      m.codicePacchetto.includes("EMVA")
    );
  },
  setCoverageMonthsForNucleoFamiliareFull(state, months) {
    state.coverageMonthsForNucleoFamiliareFull = months;
  }
};

const actions = {
  async getPreviousMonths({ commit, getters, rootGetters }) {
    try {
      commit("loading/openLoadingDialog", null, {
        root: true
      });
      if (!getters["getPreviousMonths"].length) {
        let today = dayjs(new Date());
        let yearAgo = today.subtract(11, "month");
        let aMonthAgo = today.subtract(1, "month");
        let result = await services.coverageService.getCoverages({
          memberFiscalCode: rootGetters["login/userInfo"].codiceFiscale,
          fromDate: yearAgo.format("YYYY/MM/DD"),
          toDate: aMonthAgo.format("YYYY/MM/DD")
        });
        let canAskForCoverage = result.canAskForCoverage;
        commit("setCanAskForCoverage", canAskForCoverage);
        let coverageList = result ? result.coverageList : [];
        let months = getFullCoveredMonths(coverageList, yearAgo, aMonthAgo);
        commit("setPreviousMonths", months);
      }
      commit("loading/closeLoadingDialog", null, {
        root: true
      });
    } catch (error) {
      commit("loading/closeLoadingDialog", null, {
        root: true
      });
      commit("error/setError", error, { root: true });
    }
  },
  async getCurrentMonths({ commit, rootGetters }, numOfMonths) {
    try {
      commit("loading/openLoadingDialog", null, {
        root: true
      });
      let today = dayjs(new Date());
      let gennaio = dayjs(`${dayjs().year()}-01-01`);
      let diffMonths = today.diff(gennaio, "month");
      let numOfMonthsAfter = gennaio.add(numOfMonths + diffMonths, "month");
      let result = await services.coverageService.getCoverages({
        memberFiscalCode: rootGetters["login/userInfo"].codiceFiscale,
        fromDate: gennaio.format("YYYY/MM/DD"),
        toDate: numOfMonthsAfter.format("YYYY/MM/DD")
      });
      let canAskForCoverage = result.canAskForCoverage;
      commit("setCanAskForCoverage", canAskForCoverage);
      let coverageList = result ? result.coverageList : [];
      let months = getFullCoveredMonths(coverageList, today, numOfMonthsAfter);
      commit("setCoverages", months);
      commit("setIsCurrentMonthCovered", months);
      // for EMVA check only the current month and the next month
      let monthsForEmva = getFullCoveredMonths(
        coverageList,
        dayjs(),
        dayjs().add(1, "month")
      );
      commit("setHasEmvaCoverage", monthsForEmva);
      // fino al mese in corso + 1
      let toMonth = dayjs(`${dayjs().year()}-${dayjs().month() + 2}-01`);
      let monthsForNucleoFamiliareFull = getFullCoveredMonths(
        coverageList,
        gennaio,
        toMonth
      );
      commit(
        "setCoverageMonthsForNucleoFamiliareFull",
        monthsForNucleoFamiliareFull
      );
      commit("loading/closeLoadingDialog", null, {
        root: true
      });
    } catch (error) {
      commit("loading/closeLoadingDialog", null, {
        root: true
      });
      commit("error/setError", error, { root: true });
    }
  },
  async getCeilingYears({ commit }) {
    try {
      commit("loading/openLoadingDialog", null, {
        root: true
      });
      let currentYear = dayjs().year();
      let previousYear = currentYear - 1;
      let years = [];
      for (let i = currentYear; i >= previousYear; i--) {
        years.push({ id: i, label: i.toString() });
      }
      commit("yearsRetrieved", years);
      commit("updateYearFilterField", [
        { id: dayjs().year(), label: dayjs().year().toString() }
      ]);
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/closeLoadingDialog", null, { root: true });
    }
  },
  async updateYearFilterFieldAndReloadCeilings(
    { commit, getters, dispatch },
    yearFilter
  ) {
    commit("updateYearFilterField", yearFilter);
    await dispatch("getCeilings", getters["getYearFilter"][0]);
  },
  async getCeilings({ commit }, year) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      let ceilings = await services.coverageService.getCeilingsWithYear(year);
      commit("ceilings", ceilings);
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/closeLoadingDialog", null, { root: true });
    }
  },
  async getPreviousMonthsForRimborsiMHAndUS({ commit, getters, rootGetters }) {
    try {
      commit("loading/openLoadingDialog", null, {
        root: true
      });
      if (!getters["getPreviousMonthsForRimborsiMHAndUS"].length) {
        let today = dayjs(new Date());
        let yearAgo = today.subtract(11, "month");
        let result = await services.coverageService.getCoverages({
          memberFiscalCode: rootGetters["login/userInfo"].codiceFiscale,
          fromDate: yearAgo.format("YYYY/MM/DD"),
          toDate: today.format("YYYY/MM/DD")
        });
        let canAskForCoverage = result.canAskForCoverage;
        commit("setCanAskForCoverage", canAskForCoverage);
        let coverageList = result ? result.coverageList : [];
        let months = getFullCoveredMonths(coverageList, yearAgo, today);
        commit("setPreviousMonthsForRimborsiMHAndUS", months);
      }
      commit("loading/closeLoadingDialog", null, {
        root: true
      });
    } catch (error) {
      commit("loading/closeLoadingDialog", null, {
        root: true
      });
      commit("error/setError", error, { root: true });
    }
  }
};

function getFullCoveredMonths(coverages, from, to) {
  let months = [];
  let monthDate = from;
  while (monthDate.isSame(to) || monthDate.isBefore(to)) {
    let searchedMonths = coverages.filter(
      coverage =>
        monthDate.year() === coverage.year &&
        monthDate.month() === coverage.month - 1
    );
    if (searchedMonths.length > 0) {
      let result = searchedMonths.reduce((acc, curr) => {
        if (!acc.codicePacchetto) {
          acc.codicePacchetto = [];
        }
        acc.codicePacchetto.push(curr.codicePacchetto);
        acc.year = curr.year;
        acc.month = curr.month;
        acc.covered = curr.covered;
        return acc;
      }, {});
      if (
        result.codicePacchetto.includes("FE1") ||
        result.codicePacchetto.includes("FEC") ||
        result.codicePacchetto.includes("EMVA")
      ) {
        result["covered"] = true;
      } else {
        result["covered"] = false;
      }
      months.push(result);
    } else {
      months.push({
        year: monthDate.year(),
        month: monthDate.month() + 1,
        covered: false,
        canAskForCoverage: false,
        codicePacchetto: []
      });
    }
    monthDate = monthDate.add(1, "month");
  }
  return months;
}

export default {
  namespaced: true,
  strict,
  state,
  getters,
  mutations,
  actions
};
